import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from 'helpers/axios';
import { errorMessage } from 'helpers/common';
import * as Sentry from "@sentry/react";

const fetchBalanceScorecardData = createAsyncThunk('fetchBalanceScorecardData', async (dataType, { getState, rejectWithValue }) => {
  if (dataType === '' || dataType === undefined || dataType === 'reset') return {};

  try {
    const { data, status } = await api.get('/forms/balance_scorecard/data/' + dataType);
    if (status === 200) return data;
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch balance scorecard data'));
  }
});

const fetchBalanceScorecardList = createAsyncThunk('fetchBalanceScorecardList', async (_, { getState, rejectWithValue }) => {
  try {
    const { data, status } = await api.get(`/settings/balanced-scorecards`);
    if (status === 200) {
      return data;
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch balance scorecard list'));
  }
});

const fetchRegularEvaluationLevels = createAsyncThunk('fetchRegularEvaluationLevels', async (_, { getState, rejectWithValue }) => {
  try {
    const { data, status } = await api.get(`/settings/balanced-scorecards/regelbeurteilung-regular?showSetting=true`);
    if (status === 200) {
      return data.settings?.systematicObjectivePerformanceLevelGrid?.map(item => {
        const temp = {};
        for (const k in item) {
          temp[k.replace('systematicObjective', '').toLocaleLowerCase()] = item[k];
        }
        return temp;
      });
    }
  } catch (err) {
    Sentry.captureException(err);
    return rejectWithValue(errorMessage(err, 'fetch regular evaluation levels'));
  }
});

export const balanceScorecardSlice = createSlice({
  name: 'balanceScorecard',
  initialState: {
    prefilledData: {},
    fieldsets: [],
    error: null,
    list: null,
    regularEvaluationLevels: [],
    overrideReadOnly: false,
  },
  reducers: {
    updateError: (state, action) => {
      state.error = action.payload;
    },
    updatePrefilledData: (state, action) => {
      state.prefilledData = action.payload;
    },
    updateBalanceScorecardFieldsets: (state, { payload }) => {
      state.fieldsets = payload;
    },
    setOverrideReadOnly: (state, { payload }) => {
      state.overrideReadOnly = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBalanceScorecardData.fulfilled, (state, action) => {
        state.prefilledData = action.payload;
      })
      .addCase(fetchBalanceScorecardList.fulfilled, (state, action) => {
        state.list = action.payload.data;
      })
      .addCase(fetchBalanceScorecardList.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchRegularEvaluationLevels.fulfilled, (state, action) => {
        state.regularEvaluationLevels = action.payload;
      });
  }
});

export const getBalanceScorecardFieldsets = (state) => state.balanceScorecard.fieldsets;
export const getBalanceScorecardError = (state) => state.balanceScorecard.error;
export const getBalanceScorecardData = (state) => state.balanceScorecard.prefilledData;
export const getBalanceScorecardList = (state) => state.balanceScorecard.list;
export const getRegularEvaluationLevels = (state) => state.balanceScorecard.regularEvaluationLevels;
export const getBalanceScorecardOverrideReadOnly = (state) => state.balanceScorecard.overrideReadOnly;

export { fetchBalanceScorecardData, fetchBalanceScorecardList, fetchRegularEvaluationLevels };

export const { updateError, updatePrefilledData, updateBalanceScorecardFieldsets, setOverrideReadOnly } = balanceScorecardSlice.actions;

export default balanceScorecardSlice.reducer;
